import React, { useState, useEffect } from 'react'
import { Bell, Mail, MessageSquare, Clock, Calendar, Award, CheckCircle } from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'

interface NotificationPreferences {
  email_enabled: boolean
  push_enabled: boolean
  chore_assigned: boolean
  chore_due_soon: boolean
  chore_completed: boolean
  chore_verified: boolean
  reward_redeemed: boolean
  weekly_summary: boolean
  quiet_hours_enabled: boolean
  quiet_hours_start: string
  quiet_hours_end: string
  morning_reminder: boolean
  afternoon_reminder: boolean
}

export default function NotificationSettings() {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    email_enabled: true,
    push_enabled: false,
    chore_assigned: true,
    chore_due_soon: true,
    chore_completed: true,
    chore_verified: true,
    reward_redeemed: true,
    weekly_summary: true,
    quiet_hours_enabled: false,
    quiet_hours_start: '22:00',
    quiet_hours_end: '08:00',
    morning_reminder: true,
    afternoon_reminder: true
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadPreferences()
  }, [])

  const loadPreferences = async () => {
    try {
      const response = await apiClient.get('/api/user/notification-preferences')
      if (response) {
        setPreferences(response)
      }
    } catch (error) {
      console.error('Failed to load notification preferences:', error)
    } finally {
      setLoading(false)
    }
  }

  const savePreferences = async () => {
    setSaving(true)
    try {
      await apiClient.put('/api/user/notification-preferences', preferences)
      toast.success('Notification preferences saved!')
    } catch (error) {
      toast.error('Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const handleChange = (key: keyof NotificationPreferences, value: any) => {
    setPreferences(prev => ({
      ...prev,
      [key]: value
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Notification Channels */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-blue-500" />
          Notification Channels
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Mail className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Email Notifications</div>
                <div className="text-sm text-gray-500">Receive notifications via email</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.email_enabled}
              onChange={() => handleToggle('email_enabled')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Push Notifications</div>
                <div className="text-sm text-gray-500">Browser push notifications</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.push_enabled}
              onChange={() => handleToggle('push_enabled')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <MessageSquare className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium text-gray-400">SMS Notifications</div>
                <div className="text-sm text-gray-400">Coming soon</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={false}
              disabled
              className="w-5 h-5 text-gray-300 rounded cursor-not-allowed"
            />
          </label>
        </div>
      </div>

      {/* Notification Types */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Calendar className="w-5 h-5 mr-2 text-purple-500" />
          Notification Types
        </h3>
        
        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Bell className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Chore Assigned</div>
                <div className="text-sm text-gray-500">Notify when chores are assigned</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.chore_assigned}
              onChange={() => handleToggle('chore_assigned')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Clock className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Chore Due Soon</div>
                <div className="text-sm text-gray-500">Remind about upcoming due chores</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.chore_due_soon}
              onChange={() => handleToggle('chore_due_soon')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Chore Completed</div>
                <div className="text-sm text-gray-500">Notify parents when chores are done</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.chore_completed}
              onChange={() => handleToggle('chore_completed')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Chore Verified</div>
                <div className="text-sm text-gray-500">Notify kids when a parent verifies their chore</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.chore_verified}
              onChange={() => handleToggle('chore_verified')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Award className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Rewards Redeemed</div>
                <div className="text-sm text-gray-500">Notify when rewards are redeemed</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.reward_redeemed}
              onChange={() => handleToggle('reward_redeemed')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div className="flex items-center">
              <Mail className="w-5 h-5 mr-3 text-gray-400" />
              <div>
                <div className="font-medium">Weekly Summary</div>
                <div className="text-sm text-gray-500">Email summary every Sunday</div>
              </div>
            </div>
            <input
              type="checkbox"
              checked={preferences.weekly_summary}
              onChange={() => handleToggle('weekly_summary')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>
        </div>
      </div>

      {/* Reminder Schedule */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Clock className="w-5 h-5 mr-2 text-green-500" />
          Schedule
        </h3>

        <div className="space-y-4">
          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium">Morning Reminder</div>
              <div className="text-sm text-gray-500">Daily morning chore reminder</div>
            </div>
            <input
              type="checkbox"
              checked={preferences.morning_reminder}
              onChange={() => handleToggle('morning_reminder')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <label className="flex items-center justify-between">
            <div>
              <div className="font-medium">Afternoon Reminder</div>
              <div className="text-sm text-gray-500">Daily afternoon chore reminder</div>
            </div>
            <input
              type="checkbox"
              checked={preferences.afternoon_reminder}
              onChange={() => handleToggle('afternoon_reminder')}
              className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
            />
          </label>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-center justify-between mb-3">
              <div>
                <div className="font-medium">Quiet Hours</div>
                <div className="text-sm text-gray-500">Suppress notifications during these hours</div>
              </div>
              <input
                type="checkbox"
                checked={preferences.quiet_hours_enabled}
                onChange={() => handleToggle('quiet_hours_enabled')}
                className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
              />
            </label>

            {preferences.quiet_hours_enabled && (
              <div className="grid grid-cols-2 gap-4 ml-0">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_start}
                    onChange={(e) => handleChange('quiet_hours_start', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End</label>
                  <input
                    type="time"
                    value={preferences.quiet_hours_end}
                    onChange={(e) => handleChange('quiet_hours_end', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={savePreferences}
          disabled={saving}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            'Save Preferences'
          )}
        </button>
      </div>
    </div>
  )
}